import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import caseOneNavBefore from '../images/case-one/Before_navbar_with_discussions_light.webp';
import caseOneNavAfter from '../images/case-one/After_navbar_with_discussion_light_crop2.webp';
import caseOneNavCollapsed from '../images/case-one/After_navbar_collapsed.webp';
import caseOneOverview from '../images/case-one/All-apps-management-current-starred.webp';
import caseTwoBefore from '../images/case-two/Before_stacked.webp';
import caseTwoAfterDark from '../images/case-two/After_stacked_dark.webp';
import caseThreeBefore from '../images/case-three/Before_form_fields.webp';
import caseThreeAfter from '../images/case-three/After_form_fields.webp';
import caseThreeControlsMaterial from '../images/case-three/Material_Selection_controls.webp';
import caseThreeControlsChakra from '../images/case-three/ChakraUI_Selection_controls_cropped.webp';

const cases = [
	{
		id: 'case-one',
		navLabel: '01',
		title: 'Redesigning navigation for apps and multiworkspace use',
		meta: 'Hailer · 2026',
		tags: ['B2B SaaS', 'Product design', 'Frontend', 'Backend'],
		figures: [
			{
				images: [
					{
						src: caseOneNavBefore,
						alt: 'The original Hailer sidebar navigation, emphasising core apps, with Discussions open',
						label: 'Before',
						flex: 1256 / 1632,
					},
					{
						src: caseOneNavAfter,
						alt: 'The redesigned favourite-based sidebar navigation, expanded, with Discussions open',
						label: 'After',
						flex: 697 / 906,
					},
					{
						src: caseOneNavCollapsed,
						alt: 'The redesigned sidebar navigation in its collapsed state',
						label: '',
						flex: 58 / 911,
					},
				],
				caption:
					'The sidebar navigation before and after the redesign, shown expanded and collapsed with Discussions open.',
			},
		],
		sections: [
			{
				heading: 'Context',
				body: [
					'Hailer is a business and enterprise management platform built on linked data and contextual discussions. Users can work with the underlying data through Hailer’s core views, including tables, Kanban boards, calendars and discussion-based views.',
					'When those standard views are not enough, users can build custom apps with Hailer’s SDK, with or without AI. These apps provide more tailored ways to present and interact with the same data, for example through dashboards, customer portals, web shops or booking calendars. Apps can also be made public, allowing organizations to expose selected data and services outside Hailer.',
					'**As workspace apps became more important to how customers used the product, the existing navigation no longer reflected the tools users actually relied on.** Some users worked mainly in one or two workspace apps and rarely used the core apps, while others worked across several apps and workspaces. Moving from an app in one workspace to an app in another required four clicks.',
					'The existing structure also caused problems for new users. A user could land directly in their assigned default workspace app, but the app was not shown as selected in the navigation. The navigation instead emphasized unrelated core apps, and users could struggle to find their way back after navigating elsewhere.',
					'These issues came from customer feedback as well as our own daily use of Hailer, which made the friction easy to verify in real workflows.',
					'The redesign needed to reduce this friction while preparing the product for a future multiworkspace model.',
				],
			},
			{
				heading: 'My role',
				list: [
					'Owned the UX and UI design',
					'Defined the new navigation and app-management model',
					'Implemented the entire frontend solution',
					'Designed and developed the app icon generator across frontend and backend',
					'Worked through permissions, legacy product structures and existing-user onboarding for the new personalised navigation',
					'I refined the requirements with the Product Owner, discussed the solution with senior developers, and iterated on it during six weeks of internal testing in a staging environment',
				],
			},
			{
				heading: 'Problem & constraints',
				groups: [
					{
						heading: 'User experience',
						list: [
							'The navigation prioritised Hailer’s core apps, even though many users mainly worked in workspace apps',
							'New users could land in their assigned workspace app while the navigation showed unrelated core apps and users tend to get sidetracked to there and not find their way back to their workspace app',
							'Moving between apps across workspaces required four clicks',
						],
					},
					{
						heading: 'Product and system',
						list: [
							'Different users needed different combinations of apps and workspaces',
							'Navigation needed to support future multiworkspace',
							'Existing custom roles still had to work during the transition',
						],
					},
					{
						heading: 'Supporting issues',
						list: [
							'Launching and managing apps happened in separate locations',
							'Existing app icons were inconsistent and worked poorly in dark mode',
							'Existing users needed help with moved and renamed features',
						],
					},
				],
			},
			{
				heading: 'What I built',
				figures: [
					{
						src: caseOneOverview,
						alt: 'The redesigned navigation in context: starred apps in the sidebar, the current app, the All apps panel, and app management',
						caption:
							'The redesigned navigation in dark mode, showing the expanded All apps panel with starred apps, current app and app management.',
					},
				],
				groups: [
					{
						heading: 'Personalized navigation',
						groups: [
							{
								heading: 'One favourite model',
								body: [
									'I replaced the navigation based on a fixed set of core apps with one based on the user’s favourite apps. Users can favourite both Hailer apps and workspace apps, creating a navigation that reflects the tools they use most frequently.',
									'One of the main design challenges was deciding how users would both add apps to the navigation and mark important apps for filtering in All apps. These could have been separate features, but that would have added unnecessary complexity. I used a single star action for both: starring an app adds it to the navigation and includes it in the favourites filter. A star also matched Hailer’s existing convention for personal favourites. A pin would have conflicted with Feed, where pinning affects everyone.',
								],
							},
							{
								heading: 'Keeping users oriented',
								body: [
									'The navigation displays up to ten favourite apps to keep it readable. Users can still favourite more apps across their workspaces and find them using the favourites filter in All apps.',
									'The currently open app is also shown in the navigation, even when the user has not marked it as a favourite. This avoids a disconnect where the navigation does not reflect the location of the content being viewed.',
									'The navigation also preserves “ghost apps”: apps that were previously starred but are no longer available because the user lost access or the app was removed. Instead of silently disappearing, they remain visible and explain what happened when opened.',
								],
							},
							{
								heading: 'Starting with one default app',
								body: [
									'For both new and existing users, I chose Discussions as the only favourite app shown by default. New users receive welcome messages there, so it needed to remain visible.',
									'Using the same default for everyone also avoided a technically complex setup where new and existing users would require different defaults based on permissions and previous navigation states.',
									'Showing Discussions as already favourited also demonstrates how the new model works, supported by the helper text in All apps.',
								],
							},
							{
								heading: 'Different users and screen sizes',
								body: [
									'I designed an expanded and collapsed desktop navigation, as well as a separate mobile solution, so the same personalised app model works across screen sizes.',
									'The navigation adapts to each user’s permissions. Available apps, actions and section headings are shown only when relevant. Existing custom roles controlling access to individual Hailer apps also had to remain supported while that model was being deprecated for multiworkspace.',
								],
							},
							{
								heading: 'Designed for multiworkspace',
								body: [
									'The structure was designed to continue working when the current workspace selector is eventually removed, avoiding another full navigation redesign later.',
								],
							},
							{
								heading: 'Modernising the visual language',
								body: [
									'The old sidebar was designed for only a few apps and no longer had enough space for Hailer’s growing app ecosystem. Widening it also made its heavy dark backgrounds feel increasingly dated and disconnected from the rest of the product. I redesigned the layout and visual style to support more apps while still fitting naturally alongside Hailer’s partially legacy UI.',
								],
							},
						],
					},
					{
						heading: 'All apps panel',
						body: [
							'I created All apps as the central place where users can find, launch and favourite all Hailer and workspace apps available to them.',
							'I chose a panel instead of a separate page because All apps is part of navigation. Users can open the panel, choose an app and continue working without first navigating to a separate Apps page.',
							'Apps are grouped by workspace and can be searched or filtered by favourites, giving users access to a larger collection without overloading the navigation.',
							'I also moved app management from a separate Apps Management page into All apps. Depending on their permissions, users can create apps, edit details, manage permissions and configurations, and remove apps from the same place where they are found and launched.',
						],
					},
					{
						heading: 'App icon system and generator',
						body: [
							'I designed and implemented a new icon system for Hailer and workspace apps, including an icon generator for workspace apps.',
							'Previously, users were expected to upload their own image. Most skipped this step and kept the default Hailer logo, which gave apps little identity, made the app collection visually repetitive and did not work properly in dark mode.',
							'The new generator provides a curated selection of icons and gradient colours. Users can choose a combination themselves or let the system generate one automatically.',
							'This removed the option to upload any image, but we prioritised an easier process and a consistent visual style because most users had not used the previous flexibility.',
						],
					},
				],
			},
			{
				heading: 'Existing-user onboarding',
				body: [
					'I added a one-time onboarding card for existing users, highlighting the three changes most likely to cause confusion: the new All apps panel, the renaming of Activities to Data Core, and the new location of Workspace settings. I also added helper text explaining that favouriting apps adds them to the navigation.',
				],
			},
			{
				heading: 'Accessibility',
				body: [
					'Accessibility was built into the full solution, including the navigation, All apps panel and icon generator. This included keyboard use, focus states, contrast, clear current and selected states, and accessible labels for interactive controls.',
				],
			},
			{
				heading: 'Impact',
				list: [
					'Reduced switching between apps from up to four clicks to one click for favourite apps, or two clicks through All apps, regardless of workspace',
					'Gave users a personalised navigation based on the apps they actually use',
					'Kept the current app visible in the navigation, providing a clear way back even when it was not favourited',
					'Centralised app discovery, launching, favouriting and management in All apps',
					'Improved onboarding by making the navigation match the app a user had been invited to use',
					'Established a navigation structure that supports Hailer’s move toward multiworkspace',
					'Created a consistent and recognisable icon system across Hailer core apps and workspace apps',
				],
			},
			{
				heading: 'Tools & technology',
				list: [
					'Design and research: Pen and paper, Mobbin',
					'Development: Angular, TypeScript, Node.js, Playwright',
					'AI support: Claude Code, Claude Design, ChatGPT',
					'Documentation and workflow: Hailer, Obsidian',
				],
			},
		],
	},
	{
		id: 'case-two',
		navLabel: '02',
		title: 'Designing a stackable drawer interface for complex workflows',
		meta: 'Hailer · 2026',
		tags: ['B2B SaaS', 'UX design', 'UI design', 'Frontend'],
		figures: [
			{
				images: [
					{
						src: caseTwoBefore,
						alt: 'The previous drawer view, with only a back arrow at the top to return to the previous item',
						label: 'Before',
						flex: 868 / 1649,
					},
					{
						src: caseTwoAfterDark,
						alt: 'The redesigned drawer with tabs at the top showing each item in the stack',
						label: 'After',
						flex: 867 / 1648,
					},
				],
				caption:
					'Before, a single back arrow was the only way out, so users had to remember where they came from. After, tabs at the top make the whole stack visible and let users move between items directly.',
			},
		],
		sections: [
			{
				heading: 'Context',
				body: [
					'Hailer is a SaaS platform used by organizations to manage communication and workflows across teams. The product relied heavily on a drawer-based UI for handling activities, but the existing solution only allowed one drawer to be open at a time. This limited workflows and made it difficult for users to move between items or keep track of what they had previously opened.',
					'A redesign was needed to support more flexible workflows while keeping the interface clear and manageable. This required defining a new interaction pattern beyond the existing design system.',
				],
			},
			{
				heading: 'My role',
				list: [
					'Took over the UX, UI and frontend refinement of an early functional drawer stack',
					'Worked closely with the full-stack developer who owned the underlying stack functionality, persistence and cross-view behaviour',
					'Implemented the user-facing interface and interaction details in the frontend',
				],
			},
			{
				heading: 'Problem & constraints',
				list: [
					'Needed to support working across multiple items without overwhelming the user',
					'No existing design pattern to directly follow',
					'Had to keep the UI clear even as complexity increased',
					'Required to work across desktop and mobile layouts',
					'Needed to integrate with existing product structure and workflows',
				],
			},
			{
				heading: 'What I built',
				list: [
					'The complete visual and interaction design for the drawer stack',
					'Tab navigation, active states and visual hierarchy for switching between drawers',
					'Transitions and animations that made changes in the stack understandable',
					'Overflow handling and controls for larger stacks',
					'Responsive layouts for desktop and mobile',
					'Frontend behaviour for opening the drawer in the appropriate view and keeping the collapsed stack easy to access',
				],
			},
			{
				heading: 'Impact',
				list: [
					'Made the drawer stack easier to understand and operate',
					'Gave users clearer feedback when opening, switching and closing drawers',
					'Reduced the risk of losing orientation in complex workflows',
					'Made the interaction consistent across desktop and mobile',
					'Brought the early functional solution to a production-ready level',
				],
			},
		],
	},
	{
		id: 'case-three',
		navLabel: '03',
		title: 'Scaling a design system across platforms (Angular + React)',
		meta: 'Hailer · 2025',
		tags: ['B2B SaaS', 'Design systems', 'Accessibility', 'Frontend'],
		figures: [
			{
				images: [
					{
						src: caseThreeBefore,
						alt: 'Form fields before the design system work: many inconsistently styled fields across the product',
						label: 'Before',
						flex: 756 / 945,
					},
					{
						src: caseThreeAfter,
						alt: 'Form fields after: two variants, filled and outlined, with size options, including search bars and selects',
						label: 'After',
						flex: 759 / 948,
					},
				],
				caption:
					'Form fields were one of the most ad-hoc styled components, with many different looking fields across the product. The redesign reduced them to two variants — filled and outlined — with size options, and brought search bars and select fields onto the same styles.',
			},
		],
		sections: [
			{
				heading: 'Context',
				body: [
					'Hailer is a SaaS platform used by organizations to manage communication and workflows across teams. The product had inconsistent UI patterns and fragmented styling across components, which slowed down development and created an uneven user experience. The frontend was based on an older Angular version and lacked a scalable theming approach.',
					'At the same time, the platform introduced support for building apps with React, but there were no shared styles or guidelines for these extensions. This led to inconsistent UI quality, as app developers needed a simple way to create interfaces that matched the core product.',
				],
			},
			{
				heading: 'My role',
				list: [
					'Owned the design system development and theming approach',
					'Led the UI layer modernization as part of the Angular 14 to 19 upgrade',
					'Took ownership of accessibility across UI components',
					'Defined UI, UX, and accessibility standards for the team',
					'Enabled more consistent frontend implementation across Angular and React-based applications',
				],
			},
			{
				heading: 'What I did',
				groups: [
					{
						heading: 'Design system',
						list: [
							'Rebuilt the theming system for Angular Material and defined a theming approach for a React application, including selecting and adopting Chakra UI',
							'Defined consistent styles for UI components, including missing Material components',
							'Redefined button hierarchy by replacing multiple competing filled styles with a clearer system using filled and outlined variants, improving visual clarity and consistency',
							'Created reusable patterns to unify the UI across the product',
						],
					},
					{
						heading: 'Frontend & refactoring',
						list: [
							'Replaced legacy components with updated implementations',
							'Removed large amounts of redundant SCSS and simplified the styling architecture',
						],
					},
					{
						heading: 'Accessibility',
						list: [
							'Took ownership of accessibility across UI components',
							'Ensured accessibility standards were consistently applied during the frontend modernization',
						],
					},
					{
						heading: 'Cross-platform',
						list: [
							'Applied design system principles across both Angular (Material) and React (Chakra UI)',
							'Aligned visual language and interaction patterns across platforms',
						],
						figures: [
							{
								wide: true,
								images: [
									{
										src: caseThreeControlsMaterial,
										alt: 'Selection controls in Angular Material — checkboxes, radios and switches styled to the design system',
										label: 'Angular (Material)',
										flex: 1611 / 906,
									},
									{
										src: caseThreeControlsChakra,
										alt: 'The same selection controls in React with Chakra UI, sharing the design system style',
										label: 'React (Chakra UI)',
										flex: 1668 / 938,
									},
								],
								caption:
									'Selection controls in Angular (Material) and React (Chakra UI). The two libraries are not pixel-identical, but they share the same visual style and behaviour across platforms.',
							},
						],
					},
					{
						heading: 'Developer experience',
						list: [
							'Defined standards for UI, UX, and accessibility',
							'Created documentation and clear guidelines for using the design system',
							'Enabled developers (and AI-assisted workflows) to produce consistent UI without relying on manual styling',
							'Shifted UI implementation from designer-driven to developer-enabled workflows',
						],
					},
				],
			},
			{
				heading: 'Decisions & constraints',
				list: [
					'Replaced custom-built button components with Angular Material buttons styled to match the design system, as they better support common use cases and align with the existing component model',
					'Accepted minor differences between Angular (Material) and React (Chakra UI) implementations instead of forcing full parity',
					'Focused on incremental improvements rather than attempting a full rewrite of the existing UI',
				],
			},
			{
				heading: 'Impact',
				list: [
					'Reduced the need for ad-hoc styling and improved consistency across new features',
					'Enabled developers to build UI more independently using shared patterns and guidelines',
					'Improved overall UI consistency across both the core product and platform extensions',
					'Simplified the frontend codebase by removing redundant styles and legacy components',
					'Created a more scalable foundation for future development',
					'Freed up time to focus on design system development and more complex design problems',
				],
			},
		],
	},
];

// render a figure: a row of labelled images, or a single image, with an
// optional caption
const renderFigures = (figures) =>
	figures.map((figure, index) => (
		<figure className="case-figure" key={index}>
			{figure.images ? (
				<div
					className={`case-figure-row${
						figure.wide ? ' case-figure-row--wide' : ''
					}`}
				>
					{figure.images.map((image, imageIndex) => (
						<div
							className="case-figure-item"
							key={imageIndex}
							style={image.flex ? { flexGrow: image.flex } : undefined}
						>
							{/* always render the label line so every image aligns at the
							    same top, even where a narrow image has no room for text */}
							<span className="case-figure-label">
								{image.label || ' '}
							</span>
							<img src={image.src} alt={image.alt} loading="lazy" />
						</div>
					))}
				</div>
			) : (
				<img
					className="case-figure-single"
					src={figure.src}
					alt={figure.alt}
					loading="lazy"
				/>
			)}
			{figure.caption && <figcaption>{figure.caption}</figcaption>}
		</figure>
	));

// render **bold** segments within a paragraph string
const renderRich = (text) =>
	text
		.split('**')
		.map((part, index) =>
			index % 2 === 1 ? (
				<strong key={index} className="case-emph">
					{part}
				</strong>
			) : (
				part
			)
		);

const Cases = () => {
	const location = useLocation();

	// keep these pages out of search engines
	useEffect(() => {
		const meta = document.createElement('meta');
		meta.name = 'robots';
		meta.content = 'noindex, nofollow';
		document.head.appendChild(meta);
		return () => {
			document.head.removeChild(meta);
		};
	}, []);

	// scroll to a deep-linked case (e.g. /cases#case-two) once unlocked
	useEffect(() => {
		if (location.hash) {
			const target = document.getElementById(location.hash.slice(1));
			if (target) {
				target.scrollIntoView();
			}
		}
	}, [location.hash]);

	return (
		<div className="cases">
			<header className="sticky-toolbar">
				<nav className="cases-topnav">
					<Link className="cases-home" to="/">
						Home
					</Link>
					<div className="pages-nav">
						<span className="cases-nav-label">Projects</span>
						{cases.map((item) => (
							<a key={item.id} href={`#${item.id}`}>
								{item.navLabel}
							</a>
						))}
					</div>
				</nav>
			</header>

			<main className="cases-main">
				{cases.map((item) => (
					<section className="case" id={item.id} key={item.id}>
						<p className="eyebrow">
							Project {item.navLabel} · {item.meta}
						</p>
						<h2 className="case-title">{item.title}</h2>

						{item.tags.length > 0 && (
							<ul className="project-tags case-tags">
								{item.tags.map((tag) => (
									<li key={tag}>{tag}</li>
								))}
							</ul>
						)}

						{item.figures && renderFigures(item.figures)}

						{item.sections.map((section) => (
							<div className="case-block" key={section.heading}>
								<h3>{section.heading}</h3>
								{section.figures && renderFigures(section.figures)}
								{section.body &&
									section.body.map((paragraph, index) => (
										<p key={index}>{renderRich(paragraph)}</p>
									))}
								{section.list && (
									<ul className="case-list">
										{section.list.map((point, index) => (
											<li key={index}>{point}</li>
										))}
									</ul>
								)}
								{section.groups &&
									section.groups.map((group) => (
										<div className="case-group" key={group.heading}>
											<h4>{group.heading}</h4>
											{group.body &&
												group.body.map((paragraph, index) => (
													<p key={index}>{renderRich(paragraph)}</p>
												))}
											{group.list && (
												<ul className="case-list">
													{group.list.map((point, index) => (
														<li key={index}>{point}</li>
													))}
												</ul>
											)}
											{group.groups &&
												group.groups.map((subgroup) => (
													<div
														className="case-subgroup"
														key={subgroup.heading}
													>
														<h5>{subgroup.heading}</h5>
														{subgroup.body &&
															subgroup.body.map((paragraph, index) => (
																<p key={index}>{renderRich(paragraph)}</p>
															))}
														{subgroup.list && (
															<ul className="case-list">
																{subgroup.list.map((point, index) => (
																	<li key={index}>{point}</li>
																))}
															</ul>
														)}
														{subgroup.figures &&
															renderFigures(subgroup.figures)}
													</div>
												))}
												{group.figures && renderFigures(group.figures)}
											</div>
										))}
							</div>
						))}
					</section>
				))}
			</main>
		</div>
	);
};

export default Cases;
